import { ArrowLeft, BadgeCheck, CreditCard, Handshake, Heart, RefreshCw, Scale, ShieldCheck } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ReactLenis } from "lenis/react";
import { contactDetails, marqueeItems, principles } from "./data/moselcars";
import { buildBrandTiles, vehicleMatchesBrand, vehicleMatchesQuery } from "./data/brands";
import { FUEL_FILTERS, GEARBOX_FILTERS, buildResultsTitle, formatResultCount, vehicleMatchesFuel, vehicleMatchesGearbox } from "./data/quickFilters";
import ActiveFilters from "./components/ActiveFilters";
import BrandSearch from "./components/BrandSearch";
import QuickFilters from "./components/QuickFilters";
import AiAdvisor from "./components/AiAdvisor";
import ContactSection from "./components/ContactSection";
import FavoritesDrawer from "./components/FavoritesDrawer";
import LegalPage from "./components/LegalPage";
import SyncAdminPage from "./components/SyncAdminPage";
import SellSection from "./components/SellSection";
import VehicleCompareModal from "./components/VehicleCompareModal";
import VehicleDetailPage from "./components/VehicleDetailPage";
import HeroSlider from "./components/HeroSlider";
import Reveal from "./components/Reveal";
import SiteHeader from "./components/SiteHeader";
import VehicleCard from "./components/VehicleCard";
import VehicleModal from "./components/VehicleModal";
import "./App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const FAVORITES_KEY = "moselcars-favorites";
const SNAPSHOT_KEY = "moselcars-favorites-snapshot";
const principleIcons = { shield: ShieldCheck, badge: BadgeCheck, handshake: Handshake, finance: CreditCard };

function normalizeVehicle(vehicle) {
  const images = Array.isArray(vehicle.images) && vehicle.images.length ? vehicle.images : [vehicle.image];
  return {
    ...vehicle,
    id: vehicle.id || vehicle.source_id,
    year: vehicle.year || "—",
    sourceUrl: vehicle.source_url,
    dealerSourceUrl: vehicle.dealer_source_url,
    images,
    image: images[0],
    previewImages: images.slice(0, 3)
  };
}

function sortByPriceDesc(vehicles) {
  return [...vehicles].sort((a, b) => (priceNumber(b.price) ?? -1) - (priceNumber(a.price) ?? -1));
}

function readStoredFavorites() {
  try {
    const stored = JSON.parse(window.localStorage.getItem(FAVORITES_KEY) || "[]");
    return Array.isArray(stored) ? stored.filter((id) => typeof id === "string") : [];
  } catch {
    return [];
  }
}

function readStoredSnapshot() {
  try {
    const stored = JSON.parse(window.localStorage.getItem(SNAPSHOT_KEY) || "{}");
    return stored && typeof stored === "object" && !Array.isArray(stored) ? stored : {};
  } catch {
    return {};
  }
}

function snapshotEntry(vehicle) {
  return { title: `${vehicle.brand} ${vehicle.title}`, price: vehicle.price, availability: vehicle.availability };
}

function priceNumber(value) {
  const digits = String(value || "").replace(/\D/g, "");
  return digits ? Number(digits) : null;
}

function detectChange(previous, vehicle) {
  if (!previous) return null;
  if (previous.price !== vehicle.price) {
    const before = priceNumber(previous.price);
    const after = priceNumber(vehicle.price);
    return { type: "price", from: previous.price, to: vehicle.price, direction: before !== null && after !== null && after < before ? "down" : "up" };
  }
  if (previous.availability !== vehicle.availability) return { type: "availability", from: previous.availability, to: vehicle.availability };
  return null;
}

function parseRoute() {
  const { pathname, search } = window.location;
  const vehicleMatch = pathname.match(/^\/fahrzeuge\/([^/]+)/);
  if (vehicleMatch) return { name: "vehicle", id: decodeURIComponent(vehicleMatch[1]) };
  if (pathname === "/fahrzeuge" || pathname === "/fahrzeuge/") return { name: "inventory" };
  if (pathname === "/impressum" || pathname === "/datenschutz") return { name: "legal", kind: pathname.slice(1) };
  if (pathname === "/admin/sync") return { name: "admin-sync" };
  if (pathname === "/merkliste") {
    const ids = (new URLSearchParams(search).get("f") || "").split(",").map((id) => id.trim()).filter(Boolean);
    return { name: "shared", ids };
  }
  return { name: "home" };
}

function formatSyncTime(value) {
  if (!value) return "noch nicht synchronisiert";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "noch nicht synchronisiert";
  return new Intl.DateTimeFormat("de-DE", { dateStyle: "medium", timeStyle: "short" }).format(date);
}

function SectionHeading({ kicker, title, children, id }) {
  return <div className="section-heading"><div><span className="section-kicker">{kicker}</span><h2 id={id}>{title}</h2></div><p>{children}</p></div>;
}

function Marquee({ items }) {
  const repeated = [...items, ...items];
  return <section className="marquee-band" aria-label="Moselcars Kollektion" data-testid="editorial-marquee"><div className="marquee-track">{repeated.map((item, index) => <span key={`${item}-${index}`}>{item}<b>✦</b></span>)}</div></section>;
}

export default function App() {
  const [filter, setFilter] = useState("alle");
  const [inventory, setInventory] = useState([]);
  const [inventoryState, setInventoryState] = useState({ syncStatus: "idle", sourceUrl: contactDetails.sourceUrl });
  const [isLoading, setIsLoading] = useState(true);
  const [requestError, setRequestError] = useState("");
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [comparedIds, setComparedIds] = useState([]);
  const [compareOpen, setCompareOpen] = useState(false);
  const [favoriteIds, setFavoriteIds] = useState(readStoredFavorites);
  const [favoritesOpen, setFavoritesOpen] = useState(false);
  const [snapshot, setSnapshot] = useState(readStoredSnapshot);
  const [brandFilter, setBrandFilter] = useState("");
  const [fuelFilter, setFuelFilter] = useState("");
  const [gearboxFilter, setGearboxFilter] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [contactVehicleDraft, setContactVehicleDraft] = useState("");
  const [contactAppointment, setContactAppointment] = useState(null);
  const [route, setRoute] = useState(parseRoute);
  const detailVehicleId = route.name === "vehicle" ? route.id : "";

  const loadInventory = useCallback(async () => {
    setIsLoading(true);
    setRequestError("");
    try {
      const response = await fetch(`${BACKEND_URL}/api/vehicles`);
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.detail || "Der Live-Bestand ist momentan nicht erreichbar.");
      setInventory(Array.isArray(payload.vehicles) ? sortByPriceDesc(payload.vehicles.map(normalizeVehicle)) : []);
      setInventoryState(payload);
    } catch (error) {
      setRequestError(error.message || "Der Live-Bestand ist momentan nicht erreichbar.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInventory();
  }, [loadInventory]);

  useEffect(() => {
    window.localStorage.setItem(FAVORITES_KEY, JSON.stringify(favoriteIds));
  }, [favoriteIds]);

  useEffect(() => {
    window.localStorage.setItem(SNAPSHOT_KEY, JSON.stringify(snapshot));
  }, [snapshot]);

  useEffect(() => {
    if (!inventory.length) return;
    setSnapshot((current) => {
      const next = { ...current };
      let changed = false;
      favoriteIds.forEach((id) => {
        const vehicle = inventory.find((entry) => entry.id === id);
        if (vehicle && !next[id]) {
          next[id] = snapshotEntry(vehicle);
          changed = true;
        }
      });
      return changed ? next : current;
    });
  }, [inventory, favoriteIds]);

  useEffect(() => {
    const handlePopState = () => setRoute(parseRoute());
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  const scrollToHash = (hash) => {
    let attempts = 0;
    const tryScroll = () => {
      const target = document.querySelector(hash);
      if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
      else if (attempts++ < 20) window.setTimeout(tryScroll, 60);
    };
    window.setTimeout(tryScroll, 40);
  };

  const navigate = (path) => {
    window.history.pushState({}, "", path);
    setRoute(parseRoute());
    const hash = path.includes("#") ? path.slice(path.indexOf("#")) : "";
    if (hash) scrollToHash(hash);
    else window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    if (window.location.hash && window.location.pathname === "/") scrollToHash(window.location.hash);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const comparedVehicles = useMemo(() => comparedIds.map((id) => inventory.find((vehicle) => vehicle.id === id)).filter(Boolean), [comparedIds, inventory]);
  const favoriteVehicles = useMemo(() => favoriteIds.map((id) => inventory.find((vehicle) => vehicle.id === id)).filter(Boolean), [favoriteIds, inventory]);
  const favoriteChanges = useMemo(() => Object.fromEntries(favoriteVehicles.map((vehicle) => [vehicle.id, detectChange(snapshot[vehicle.id], vehicle)]).filter(([, change]) => change)), [favoriteVehicles, snapshot]);
  const missingFavorites = useMemo(() => inventory.length && !requestError ? favoriteIds.filter((id) => snapshot[id] && !inventory.some((vehicle) => vehicle.id === id)).map((id) => ({ id, ...snapshot[id] })) : [], [favoriteIds, inventory, snapshot, requestError]);
  const favoriteAlertCount = Object.keys(favoriteChanges).length + missingFavorites.length;
  const acknowledgeFavoriteChanges = () => setSnapshot((current) => {
    const next = { ...current };
    favoriteVehicles.forEach((vehicle) => { next[vehicle.id] = snapshotEntry(vehicle); });
    return next;
  });
  const closeFavorites = () => {
    setFavoritesOpen(false);
    acknowledgeFavoriteChanges();
  };
  const sharedVehicles = useMemo(() => route.name === "shared" ? route.ids.map((id) => inventory.find((vehicle) => vehicle.id === id)).filter(Boolean) : null, [route, inventory]);
  const detailVehicle = inventory.find((vehicle) => vehicle.id === detailVehicleId);
  const openVehiclePage = (vehicle) => {
    setFavoritesOpen(false);
    setSelectedVehicle(null);
    navigate(`/fahrzeuge/${encodeURIComponent(vehicle.id)}`);
  };
  const closeVehiclePage = () => navigate("/fahrzeuge");
  const closeSharedList = () => navigate("/");
  const adoptSharedList = () => {
    setFavoriteIds((current) => [...current, ...(sharedVehicles || []).map((vehicle) => vehicle.id).filter((id) => !current.includes(id))]);
    navigate("/");
    setFavoritesOpen(true);
  };
  const showOwnList = () => {
    navigate("/");
    setFavoritesOpen(true);
  };
  const toggleCompare = (vehicleId) => setComparedIds((current) => current.includes(vehicleId) ? current.filter((id) => id !== vehicleId) : current.length < 2 ? [...current, vehicleId] : current);
  const toggleFavorite = (vehicleId) => {
    const isSaved = favoriteIds.includes(vehicleId);
    setFavoriteIds((current) => isSaved ? current.filter((id) => id !== vehicleId) : [...current, vehicleId]);
    setSnapshot((current) => {
      const next = { ...current };
      const vehicle = inventory.find((entry) => entry.id === vehicleId);
      if (isSaved) delete next[vehicleId];
      else if (vehicle) next[vehicleId] = snapshotEntry(vehicle);
      return next;
    });
  };
  const removeCompared = (vehicleId) => {
    setComparedIds((current) => current.filter((id) => id !== vehicleId));
    setCompareOpen(false);
  };
  const prepareContact = (vehicle, appointment = null) => {
    const vehicleName = `${vehicle.brand} ${vehicle.title}`;
    setContactVehicleDraft(vehicleName);
    setContactAppointment(appointment ? { ...appointment, vehicle: vehicleName } : null);
    setFavoritesOpen(false);
    if (route.name !== "home") navigate("/");
    window.setTimeout(() => document.getElementById("kontakt")?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
  };

  const matchesCondition = (vehicle) => {
    if (filter === "unfallfrei") return vehicle.condition === "Unfallfrei";
    if (filter === "hinweis") return vehicle.condition !== "Unfallfrei";
    if (filter === "merkliste") return favoriteIds.includes(vehicle.id);
    return true;
  };
  const baseVehicles = useMemo(() => inventory.filter((vehicle) => matchesCondition(vehicle) && vehicleMatchesQuery(vehicle, searchQuery)), [inventory, filter, favoriteIds, searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps
  const visibleVehicles = useMemo(() => baseVehicles.filter((vehicle) => vehicleMatchesBrand(vehicle, brandFilter) && vehicleMatchesFuel(vehicle, fuelFilter) && vehicleMatchesGearbox(vehicle, gearboxFilter)), [baseVehicles, brandFilter, fuelFilter, gearboxFilter]);
  // Contextual counts: each tile group reflects the other active filters so combinations stay transparent
  const brandContext = useMemo(() => baseVehicles.filter((vehicle) => vehicleMatchesFuel(vehicle, fuelFilter) && vehicleMatchesGearbox(vehicle, gearboxFilter)), [baseVehicles, fuelFilter, gearboxFilter]);
  const fuelContext = useMemo(() => baseVehicles.filter((vehicle) => vehicleMatchesBrand(vehicle, brandFilter) && vehicleMatchesGearbox(vehicle, gearboxFilter)), [baseVehicles, brandFilter, gearboxFilter]);
  const gearboxContext = useMemo(() => baseVehicles.filter((vehicle) => vehicleMatchesBrand(vehicle, brandFilter) && vehicleMatchesFuel(vehicle, fuelFilter)), [baseVehicles, brandFilter, fuelFilter]);
  const hasSearchFilter = Boolean(brandFilter || fuelFilter || gearboxFilter || searchQuery.trim());
  const activeBrandName = buildBrandTiles(inventory).find((tile) => tile.slug === brandFilter)?.name || "";
  const resultsTitle = buildResultsTitle({ brandName: activeBrandName, fuelSlug: fuelFilter, gearboxSlug: gearboxFilter });
  const resetSearchFilters = () => {
    setBrandFilter("");
    setFuelFilter("");
    setGearboxFilter("");
    setSearchQuery("");
  };
  const scrollToResults = () => {
    window.requestAnimationFrame(() => document.getElementById("ergebnisse")?.scrollIntoView({ behavior: "smooth", block: "start" }));
  };
  const applyBrand = (slug) => { setBrandFilter(slug); scrollToResults(); };
  const applyFuel = (slug) => { setFuelFilter(slug); scrollToResults(); };
  const applyGearbox = (slug) => { setGearboxFilter(slug); scrollToResults(); };
  const activeFilterChips = [
    brandFilter && { key: `brand-${brandFilter}`, label: activeBrandName, onRemove: () => setBrandFilter("") },
    fuelFilter && { key: `fuel-${fuelFilter}`, label: FUEL_FILTERS.find((entry) => entry.slug === fuelFilter)?.name, onRemove: () => setFuelFilter("") },
    gearboxFilter && { key: `gearbox-${gearboxFilter}`, label: GEARBOX_FILTERS.find((entry) => entry.slug === gearboxFilter)?.name, onRemove: () => setGearboxFilter("") },
    searchQuery.trim() && { key: "query", label: `„${searchQuery.trim()}“`, onRemove: () => setSearchQuery("") }
  ].filter(Boolean);

  const heroSlides = useMemo(() => inventory.slice(0, 3).map((vehicle) => ({
    id: vehicle.id,
    model: `${vehicle.brand} ${vehicle.title}`,
    edition: vehicle.edition,
    year: vehicle.year,
    image: vehicle.image,
    price: vehicle.price,
    power: vehicle.power,
    mileage: vehicle.mileage
  })), [inventory]);

  const syncMessage = inventoryState.syncStatus === "error"
    ? "Letzter Abruf fehlgeschlagen · gespeicherter Bestand bleibt sichtbar"
    : inventoryState.last_successful_sync_at
      ? `Zuletzt aktualisiert · ${formatSyncTime(inventoryState.last_successful_sync_at)}`
      : inventoryState.syncStatus === "running"
        ? "Live-Bestand wird gerade geladen"
        : "Live-Bestand wird initial geladen";
  const syncItems = [`${inventory.length || "—"} AKTUELLE ANGEBOTE`, ...marqueeItems.slice(1)];
  const emptyFilterMessage = hasSearchFilter ? `Keine Fahrzeuge für „${[activeBrandName, FUEL_FILTERS.find((entry) => entry.slug === fuelFilter)?.name, GEARBOX_FILTERS.find((entry) => entry.slug === gearboxFilter)?.name, searchQuery.trim()].filter(Boolean).join(" + ")}“ im aktuellen Bestand. Tipp: einen Filter entfernen oder den Fahrzeugberater fragen.` : filter === "merkliste" ? "Deine Merkliste ist noch leer. Tippe auf das Herz eines Fahrzeugs, um es hier zu sammeln." : "Aktuell sind keine Fahrzeuge im Live-Bestand veröffentlicht.";

  const teaserBrands = buildBrandTiles(inventory).filter((brand) => brand.count > 0).slice(0, 8);
  const inventoryPage = <>
    <section className="inventory-page-head page-shell" aria-labelledby="inventory-page-title">
      <button className="detail-back-button" type="button" onClick={() => navigate("/")} data-testid="inventory-back-button"><ArrowLeft size={16} /> Zur Startseite</button>
      <div className="inventory-page-title"><div><span className="section-kicker">LIVE-BESTAND · SCHWEICH-ISSEL</span><h1 id="inventory-page-title">Unsere <em>Fahrzeuge.</em></h1></div><p>{inventory.length ? `${inventory.length} Gebrauchtwagen` : "Gebrauchtwagen"} aus dem aktuellen Händlerbestand – stündlich aktualisiert. Suche nach Marke oder Modell, vergleiche und merke dir Favoriten.</p></div>
    </section>
    <section className="inventory-section section" id="bestand" aria-label="Aktueller Fahrzeugbestand">
            <div className="page-shell">
              <BrandSearch inventory={inventory} brandContext={brandContext} brand={brandFilter} onBrandChange={applyBrand} query={searchQuery} onQueryChange={setSearchQuery} />
              <QuickFilters fuel={fuelFilter} onFuelChange={applyFuel} gearbox={gearboxFilter} onGearboxChange={applyGearbox} fuelContext={fuelContext} gearboxContext={gearboxContext} />
              <div className="results-head" id="ergebnisse" data-testid="results-head">
                <div><span className="section-kicker">ERGEBNISSE</span><h2 data-testid="results-title">{resultsTitle}</h2><p data-testid="results-count">{formatResultCount(visibleVehicles.length)}</p></div>
                <ActiveFilters chips={activeFilterChips} onReset={resetSearchFilters} />
              </div>
              <Reveal className="inventory-toolbar">
                <div className="inventory-toolbar-info">
                  <div><span className="mono-label">{visibleVehicles.length} ANGEBOTE · {inventoryState.syncStatus === "success" ? "LIVE" : "AKTUALISIERUNG"}</span><span className="toolbar-note">Datenquelle · Händlerbestand</span></div>
                  <div className={`inventory-sync-bar sync-${inventoryState.syncStatus}`} data-testid="inventory-sync-status"><span className="sync-indicator" /><span>{syncMessage}</span><button type="button" onClick={loadInventory} disabled={isLoading} data-testid="inventory-refresh-button"><RefreshCw size={14} className={isLoading ? "is-spinning" : ""} />{isLoading ? "Lädt …" : "Neu laden"}</button></div>
                </div>
                <div className="filter-buttons" role="group" aria-label="Bestand filtern"><button className={filter === "alle" ? "is-active" : ""} type="button" onClick={() => setFilter("alle")} data-testid="filter-all-button">Alle Angebote</button><button className={filter === "unfallfrei" ? "is-active" : ""} type="button" onClick={() => setFilter("unfallfrei")} data-testid="filter-accident-free-button">Unfallfrei</button><button className={filter === "hinweis" ? "is-active" : ""} type="button" onClick={() => setFilter("hinweis")} data-testid="filter-notice-button">Hinweise</button><button className={`filter-favorites ${filter === "merkliste" ? "is-active" : ""}`} type="button" onClick={() => setFilter("merkliste")} data-testid="filter-favorites-button"><Heart size={12} fill={favoriteIds.length ? "currentColor" : "none"} /> Merkliste{favoriteIds.length ? ` (${favoriteIds.length})` : ""}</button></div>
              </Reveal>
              {requestError && inventory.length === 0 ? <div className="inventory-empty inventory-error" role="alert" data-testid="inventory-error-state"><strong>Der Live-Bestand konnte nicht geladen werden.</strong><span>{requestError}</span><button className="text-action" type="button" onClick={loadInventory} data-testid="inventory-error-retry-button">Erneut versuchen <RefreshCw size={15} /></button></div> : isLoading && inventory.length === 0 ? <div className="inventory-empty" role="status" data-testid="inventory-loading-state">Live-Fahrzeuge werden geladen …</div> : <div className="inventory-grid">{visibleVehicles.map((vehicle, index) => <Reveal key={vehicle.id} className={`vehicle-card-reveal reveal-delay-${index + 1}`}><VehicleCard vehicle={vehicle} onDetails={openVehiclePage} onPreview={setSelectedVehicle} isCompared={comparedIds.includes(vehicle.id)} onToggleCompare={toggleCompare} compareDisabled={comparedIds.length >= 2} isFavorite={favoriteIds.includes(vehicle.id)} onToggleFavorite={toggleFavorite} change={favoriteChanges[vehicle.id]} /></Reveal>)}</div>}
              {!isLoading && !requestError && visibleVehicles.length === 0 && <div className="inventory-empty" data-testid="inventory-empty-state">{emptyFilterMessage}</div>}
              <Reveal className="inventory-bottom"><span>Wähle bis zu zwei Fahrzeuge für einen direkten Vergleich.</span><span className="inventory-bottom-note">{inventoryState.last_successful_sync_at ? `Stand · ${formatSyncTime(inventoryState.last_successful_sync_at)}` : "Live-Bestand wird geladen"}</span></Reveal>
              {comparedVehicles.length > 0 && <div className="compare-tray" data-testid="vehicle-compare-tray"><div><Scale size={18} /><strong>Vergleich · {comparedVehicles.length} / 2</strong><span>{comparedVehicles.map((vehicle) => `${vehicle.brand} ${vehicle.title}`).join(" · ")}</span></div><div className="compare-tray-actions"><button className="text-action" type="button" onClick={() => setComparedIds([])} data-testid="vehicle-compare-clear-button">Auswahl leeren</button><button className="button button-primary" type="button" disabled={comparedVehicles.length !== 2} onClick={() => setCompareOpen(true)} data-testid="vehicle-compare-open-button">Vergleich öffnen <Scale size={15} /></button></div></div>}
            </div>
          </section>
  </>;

  return (
    <ReactLenis root options={{ autoRaf: true, anchors: true, respectReducedMotion: true }}>
      <div className="app-shell" id="top">
        <SiteHeader favoriteCount={favoriteIds.length} favoriteAlerts={favoriteAlertCount} onOpenFavorites={() => setFavoritesOpen(true)} onNavigate={navigate} currentPath={route.name === "inventory" ? "/fahrzeuge" : "/"} />
        <main>{route.name === "admin-sync" ? <SyncAdminPage onBack={() => navigate("/")} /> : route.name === "legal" ? <LegalPage kind={route.kind} onBack={() => navigate("/")} /> : route.name === "inventory" ? inventoryPage : detailVehicleId ? <VehicleDetailPage vehicle={detailVehicle} onBack={closeVehiclePage} onPrepareInquiry={prepareContact} isFavorite={favoriteIds.includes(detailVehicleId)} onToggleFavorite={toggleFavorite} /> : <>
          <section className="hero-section" aria-labelledby="hero-title">
            <div className="page-shell hero-layout">
              <div className="hero-copy">
                <span className="section-kicker hero-kicker">Moselcars · Live-Händlerbestand</span>
                <h1 className="hero-title" id="hero-title"><span className="hero-line line-one">Mosel Cars.</span><span className="hero-line line-two">Dein Auto.</span><span className="hero-line line-three">Dein Vertrauen.</span></h1>
                <p className="hero-lead">Aktuelle Gebrauchtwagen aus Schweich-Issel — regelmäßig direkt aus dem Händlerbestand aktualisiert.</p>
                <div className="hero-actions"><button className="button button-primary" type="button" onClick={() => navigate("/fahrzeuge")} data-testid="hero-inventory-cta-button">Fahrzeuge ansehen <span>↘</span></button><a className="button button-secondary" href="#kontakt" data-testid="hero-contact-cta-button">Anfrage senden <span>↗</span></a></div>
                <div className="hero-signature"><span>A+B Automobile</span><span className="signature-rule" /><span>Bahndamm 1 · Schweich-Issel</span></div>
              </div>
              <HeroSlider slides={heroSlides} />
            </div>
          </section>
          <Marquee items={syncItems} />
          <AiAdvisor vehicles={inventory} selectedVehicles={comparedVehicles} onPrepareInquiry={prepareContact} onOpenVehicle={openVehiclePage} />
          <section className="inventory-teaser section" id="bestand" aria-labelledby="inventory-teaser-title">
            <div className="page-shell inventory-teaser-inner">
              <Reveal><span className="section-kicker">LIVE-BESTAND</span><h2 id="inventory-teaser-title">{inventory.length ? `${inventory.length} Fahrzeuge` : "Fahrzeuge"}<br /><em>im Bestand.</em></h2><p>Suche nach Marke, Modell oder Ausstattung, vergleiche zwei Angebote und merke dir Favoriten – auf unserer Bestandsseite.</p><button className="button button-primary" type="button" onClick={() => navigate("/fahrzeuge")} data-testid="inventory-teaser-button">Zum Fahrzeugbestand <span>↘</span></button></Reveal>
              <Reveal className="inventory-teaser-brands" delay="100ms" data-testid="inventory-teaser-brands">{teaserBrands.map((brand) => <button type="button" key={brand.slug} onClick={() => { setBrandFilter(brand.slug); navigate("/fahrzeuge"); }} aria-label={`${brand.name} im Bestand ansehen`} data-testid={`teaser-brand-${brand.slug}`}>{brand.logo ? <img src={brand.logo} alt="" loading="lazy" /> : <span>{brand.name}</span>}<small>{brand.count}</small></button>)}</Reveal>
            </div>
          </section>
          <section className="about-section section" id="ueber-uns" aria-labelledby="about-title">
            <div className="page-shell about-grid">
              <Reveal className="about-image-wrap"><img src="/images/moselcars-standort.jpg" alt="Moselcars – Fahrzeuge auf dem Hof in Schweich-Issel" loading="lazy" /><span className="image-note">Bahndamm 1</span></Reveal>
              <Reveal className="about-copy" delay="100ms"><span className="section-kicker">01 / ÜBER DEN STANDORT</span><h2 id="about-title">Direkt an der<br /><em>nächsten Kurve.</em></h2><p>In Schweich-Issel finden Sie den aktuellen Fahrzeugbestand von A+B Automobile — mit kurzen Wegen, klaren Anzeigen und einem direkten Ansprechpartner.</p><p>Sie möchten ein Angebot besichtigen, Details klären oder eine Finanzierung besprechen? Schicken Sie uns Ihre Frage. Die Antwort landet direkt bei Moselcars.</p><a className="button button-secondary" href="#kontakt" data-testid="about-contact-button">Bestand anfragen <span>↗</span></a><div className="about-facts"><div><strong>{inventory.length}</strong><span>Live-Angebote</span></div><div><strong>54338</strong><span>Schweich-Issel</span></div><div><strong>1:1</strong><span>Direkter Kontakt</span></div></div></Reveal>
            </div>
          </section>
          <section className="principles-section section" id="finanzierung" aria-labelledby="principles-title">
            <div className="page-shell"><Reveal><SectionHeading kicker="02 / WARUM MOSELCARS" id="principles-title" title={<>Nicht nur<br /><em>anzeigen.</em></>}>Ein gutes Angebot beginnt mit Angaben, die man prüfen und Fragen, die man direkt stellen kann.</SectionHeading></Reveal><div className="principles-grid">{principles.map((principle, index) => { const Icon = principleIcons[principle.icon]; return <Reveal className="principle-item" delay={`${index * 70}ms`} key={principle.id} data-testid={`principle-${principle.id}`}><div className="principle-top"><span>{principle.number}</span><Icon size={22} strokeWidth={1.2} /></div><h3>{principle.title}</h3><p>{principle.text}</p></Reveal>; })}</div></div>
          </section>
          <SellSection onOpenPrivacy={() => navigate("/datenschutz")} />
          <ContactSection prefillVehicle={contactVehicleDraft} prefillAppointment={contactAppointment} onOpenPrivacy={() => navigate("/datenschutz")} />
        </>}</main>
        <footer className="site-footer"><div className="page-shell"><div className="footer-main"><a className="footer-logo" href="#top" aria-label="Moselcars – zum Seitenanfang" data-testid="footer-brand-link"><img src="/images/moselcars-logo.jpg" alt="Mosel Cars – Quality drives trust" width="1536" height="1024" loading="lazy" /></a><p>Mosel Cars. Dein Auto.<br />Dein Vertrauen.</p><div className="footer-cta"><span className="mono-label">DIREKTKONTAKT</span><a href={`mailto:${contactDetails.email}`} data-testid="footer-email-link">{contactDetails.email}</a><address data-testid="footer-address">{contactDetails.address}</address></div></div><div className="footer-bottom"><span>© 2026 A+B Automobile · Moselcars</span><div className="footer-links"><a href="/impressum" onClick={(event) => { event.preventDefault(); navigate("/impressum"); }} data-testid="impressum-link">Impressum</a><a href="/datenschutz" onClick={(event) => { event.preventDefault(); navigate("/datenschutz"); }} data-testid="datenschutz-link">Datenschutz</a><a href="https://www.instagram.com" target="_blank" rel="noreferrer" data-testid="instagram-link">Instagram</a><a href="https://www.facebook.com" target="_blank" rel="noreferrer" data-testid="facebook-link">Facebook</a></div></div><div className="footer-legal"><span>{contactDetails.company} · {contactDetails.address}</span><span>Fahrzeugdaten stündlich aus dem Händlerbestand · Alle Angaben ohne Gewähr</span></div></div></footer>
        {route.name === "shared" && !isLoading && <FavoritesDrawer vehicles={favoriteVehicles} pendingCount={0} sharedVehicles={sharedVehicles} onAdoptShared={adoptSharedList} onShowOwn={showOwnList} onClose={closeSharedList} onRemove={toggleFavorite} onOpenVehicle={openVehiclePage} onPrepareInquiry={prepareContact} />}
        {favoritesOpen && route.name !== "shared" && <FavoritesDrawer vehicles={favoriteVehicles} missing={missingFavorites} changes={favoriteChanges} pendingCount={favoriteIds.length - favoriteVehicles.length - missingFavorites.length} onClose={closeFavorites} onRemove={toggleFavorite} onOpenVehicle={openVehiclePage} onPrepareInquiry={prepareContact} />}
        {compareOpen && comparedVehicles.length === 2 && <VehicleCompareModal vehicles={comparedVehicles} onClose={() => setCompareOpen(false)} onRemove={removeCompared} />}
        {selectedVehicle && <VehicleModal vehicle={selectedVehicle} onClose={() => setSelectedVehicle(null)} />}
      </div>
    </ReactLenis>
  );
}
